/**
 * Card resumen de AgencyClient con KPIs + gestión del portal de transparencia
 * por cliente (enlace único y estable por cliente).
 */
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Building2, Mail, Phone, FileText, Globe, Copy, Check, ExternalLink, Link2,
  Pencil, Trash2,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { PortalCredentialsForm } from "@/components/feature/media/portal-credentials-form";
import { media, type AgencyClientOut } from "@/lib/api";

export function ClientCard({ client, onEdit }: { client: AgencyClientOut; onEdit?: (c: AgencyClientOut) => void }) {
  const [portalOpen, setPortalOpen] = useState(false);
  const qc = useQueryClient();
  const campaignsQ = useQuery({
    queryKey: ["media", "campaigns", "by_client", client.id],
    queryFn: () => media.listCampaigns({ client_id: client.id, limit: 200 }),
  });

  const del = useMutation({
    mutationFn: () => media.deleteClient(client.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["media", "clients"] });
      toast.success("Cliente desactivado");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const activeCampaigns = (campaignsQ.data ?? []).filter(c => c.status === "activa").length;

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="py-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            {client.logo_url ? (
              <img src={client.logo_url} alt={client.name} className="w-10 h-10 rounded-lg object-cover shrink-0" />
            ) : (
              <div className="w-10 h-10 rounded-lg bg-brand-100 dark:bg-brand-900/40 flex items-center justify-center shrink-0">
                <Building2 size={18} className="text-brand-600" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-ink-900 dark:text-white truncate">{client.name}</div>
              <div className="text-xs font-mono text-ink-500 mt-0.5">
                {client.code} · <span className="text-ink-400">/r/{client.slug}</span>
              </div>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            <Badge variant={client.is_active ? "default" : "outline"}>
              {client.is_active ? "Activo" : "Inactivo"}
            </Badge>
            {client.portal_enabled && (
              <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                <Globe size={10} className="mr-1" /> Portal activo
              </Badge>
            )}
          </div>
        </div>

        {client.industry && (
          <div className="text-xs text-ink-500 mb-3">{client.industry}</div>
        )}

        <div className="space-y-1 text-xs text-ink-500">
          {client.contact_email && (
            <div className="flex items-center gap-2">
              <Mail size={12} />
              <span className="truncate">{client.contact_email}</span>
            </div>
          )}
          {client.phone && (
            <div className="flex items-center gap-2">
              <Phone size={12} />
              <span>{client.phone}</span>
            </div>
          )}
          {client.ruc && (
            <div className="flex items-center gap-2">
              <FileText size={12} />
              <span className="font-mono">RUC {client.ruc}</span>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between mt-4 pt-3 border-t border-ink-100 dark:border-ink-800">
          <div className="text-xs">
            <div className="text-ink-500">Campañas activas</div>
            <div className="text-lg font-semibold text-ink-900 dark:text-white">
              {campaignsQ.isLoading ? "…" : activeCampaigns}
            </div>
          </div>
          <div className="text-xs">
            <div className="text-ink-500">Avisos</div>
            <div className="text-lg font-semibold text-ink-900 dark:text-white">
              {client.ad_counter}
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" onClick={() => setPortalOpen(true)}>
              <Globe size={14} /> Portal
            </Button>
            {onEdit && (
              <Button size="icon" variant="ghost" className="h-8 w-8" title="Editar" onClick={() => onEdit(client)}>
                <Pencil size={14} />
              </Button>
            )}
            <Button size="icon" variant="ghost" className="h-8 w-8 text-bad" title="Desactivar"
              disabled={del.isPending}
              onClick={() => { if (confirm(`¿Desactivar cliente "${client.name}"?`)) del.mutate(); }}>
              <Trash2 size={14} />
            </Button>
          </div>
        </div>
        <Link to="/clients/$clientId" params={{ clientId: String(client.id) }}
          className="block mt-3 text-xs text-brand-600 hover:underline">
          Ver detalle →
        </Link>
      </CardContent>

      <ClientPortalDialog client={client} open={portalOpen} onOpenChange={setPortalOpen} />
    </Card>
  );
}

function ClientPortalDialog({
  client, open, onOpenChange,
}: {
  client: AgencyClientOut;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const qc = useQueryClient();
  const [copied, setCopied] = useState(false);
  const [permanent, setPermanent] = useState(client.portal_expires_at == null);
  const [expiresDays, setExpiresDays] = useState(365);

  // URL pública limpia por slug (el backend resuelve el slug como token público).
  const portalUrl = `${window.location.origin}${media.clientPortalUrlBySlug(client.slug)}`;

  const publish = useMutation({
    mutationFn: () => media.publishClientPortal(client.id, permanent ? null : expiresDays),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["media", "clients"] });
      toast.success("Portal de transparencia publicado");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const unpublish = useMutation({
    mutationFn: () => media.unpublishClientPortal(client.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["media", "clients"] });
      toast.success("Portal despublicado");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  function copy() {
    if (!portalUrl) return;
    navigator.clipboard.writeText(portalUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Globe size={18} className="text-brand-600" /> Portal de transparencia
          </DialogTitle>
          <DialogDescription>
            Enlace público único de <strong>{client.name}</strong>. Agrega todas sus
            inserciones y los reportes publicados del cliente. Compártelo con el
            cliente o auditor{client.portal_login_required
              ? " — requiere login con las credenciales configuradas."
              : " — acceso abierto por enlace (puedes exigir login abajo)."}
          </DialogDescription>
        </DialogHeader>

        {client.portal_enabled && portalUrl ? (
          <div className="space-y-3">
            <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 text-xs text-emerald-700 dark:text-emerald-300">
              <div className="font-semibold flex items-center gap-1.5">
                <Check size={13} /> Portal activo
              </div>
              <div className="mt-1">
                Publicado: {client.portal_published_at ? new Date(client.portal_published_at).toLocaleDateString("es-PE") : "—"}
                {" · "}
                {client.portal_expires_at
                  ? `Expira: ${new Date(client.portal_expires_at).toLocaleDateString("es-PE")}`
                  : "Enlace permanente"}
              </div>
            </div>
            <div>
              <Label className="text-xs flex items-center gap-1.5"><Link2 size={12} /> Enlace del portal</Label>
              <div className="flex gap-2 mt-1">
                <Input value={portalUrl} readOnly className="font-mono text-xs" />
                <Button variant="outline" size="icon" onClick={copy} title="Copiar">
                  {copied ? <Check size={14} /> : <Copy size={14} />}
                </Button>
                <Button variant="outline" size="icon" asChild title="Abrir">
                  <a href={portalUrl} target="_blank" rel="noopener noreferrer">
                    <ExternalLink size={14} />
                  </a>
                </Button>
              </div>
            </div>
            <div className="flex justify-between items-center pt-1">
              <Button variant="ghost" size="sm" onClick={() => publish.mutate()} disabled={publish.isPending}>
                Renovar vigencia
              </Button>
              <Button variant="destructive" size="sm" onClick={() => unpublish.mutate()} disabled={unpublish.isPending}>
                Despublicar
              </Button>
            </div>
            <PortalCredentialsForm client={client} />
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-ink-500">
              El portal de este cliente aún no está publicado.
              {client.portal_token && " Re-publicar reusa el mismo enlace anterior."}
            </p>
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input type="checkbox" checked={permanent}
                onChange={(e) => setPermanent(e.target.checked)} />
              Enlace permanente (sin expiración)
            </label>
            {!permanent && (
              <div>
                <Label className="text-xs">Vigencia (días)</Label>
                <Input type="number" min={1} max={3650}
                  value={expiresDays} onChange={(e) => setExpiresDays(Number(e.target.value))} />
              </div>
            )}
            <Button onClick={() => publish.mutate()} disabled={publish.isPending} className="w-full">
              <Globe size={14} /> {publish.isPending ? "Publicando…" : "Publicar portal de transparencia"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
